import { get, patch, post } from '../../lib/api/client';
import { downloadExport, saveBlob } from '../../lib/api/download';
import { fromP1, toP1Query, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';
import type { AdminProfileRow } from '../profiles/api';

/**
 * Campaigns, their short links and QR codes (BE `admin-campaigns.go`,
 * `admin-qr.go`; BE reqs §3.4, §4.1).
 *
 * Every link anyone handles is a short link `<appDomain>/r/{ref}` — one per
 * variant and language, and one per QR code. The long landing URL (target +
 * UTM tuple) is built by the BE when a link is opened, so it never appears
 * here.
 */

export type CampaignType = 'chef_recruitment' | 'user_acquisition' | 'event';
/** Draft: links land on the target with no UTM and count nothing until activated. */
export type CampaignStatus = 'draft' | 'active' | 'archived';
export type CampaignChannel = 'qrcode' | 'email' | 'social' | 'print' | 'sms' | 'event' | 'referral' | 'paid';
/** A breakdown bucket: the channel enum plus `other` for any unknown utm_medium. */
export type ChannelBucket = CampaignChannel | 'other';

export const CAMPAIGN_TYPES: CampaignType[] = ['chef_recruitment', 'user_acquisition', 'event'];
export const CAMPAIGN_STATUSES: CampaignStatus[] = ['draft', 'active', 'archived'];
export const CAMPAIGN_CHANNELS: CampaignChannel[] = [
  'qrcode',
  'email',
  'social',
  'print',
  'sms',
  'event',
  'referral',
  'paid',
];

export interface VariantEvent {
  action: 'added' | 'described' | 'disabled' | 'enabled';
  at: string;
  byProfileId: string;
  description?: string;
}

export interface CampaignVariant {
  /** utm_content: a, b, c… assigned by the BE, never reused. */
  value: string;
  description: string;
  /** Hidden in the admin only; its links keep working. */
  disabled: boolean;
  events: VariantEvent[];
}

export interface Campaign {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** The campaign ID (`kermes_092026`) — also utm_campaign. Immutable. */
  slug: string;
  name: string;
  type: CampaignType;
  channel: CampaignChannel;
  localeId?: string;
  localeName?: string;
  status: CampaignStatus;
  startsAt?: string;
  endsAt?: string;
  deactivatedRedirectUrl: string;
  targetUrl: string;
  utmSource: string;
  utmTerm: string;
  variants: CampaignVariant[];
  variantCount: number;
  /** Saved without variants: the plain link (no utm_content) exists. */
  hasBaseLink: boolean;
  signupCount: number;
}

/** A copyable short link. `variant` '' = the base link; `lang` '' = English. */
export interface CampaignLink {
  ref: string;
  shortUrl: string;
  variant: string;
  description: string;
  disabled: boolean;
  lang: string;
  hitCount: number;
  lastHitAt?: string;
}

export interface QRInventoryRow {
  qrCodeId: string;
  type: string;
  /** The campaign variant (utm_content) for admin codes; '' = the base link. */
  placement: string;
  color: string;
  lang: string;
  targetUrl: string;
  /** The branded PNG. */
  publicUrl: string;
  ref?: string;
  shortUrl?: string;
  campaignId?: string;
  campaignSlug?: string;
  campaignName?: string;
  campaignStatus?: string;
  localeId?: string;
  localeName?: string;
  /** The connection a marketing code makes; absent on campaign codes. */
  connectionLevel?: number;
  connectionGroup?: string;
  connectionProfile?: string;
  connectionProfileName?: string;
  scans: number;
  conversionCount: number;
  /** Percent: conversions over scans. */
  conversionRatePct: number;
  createdAt: string;
}

export interface CampaignDetail {
  campaign: Campaign;
  qrCodes: QRInventoryRow[] | null;
  links: CampaignLink[] | null;
  /** Visits, link hits or scans exist — type, channel, locale, source and term are then fixed. */
  hasTraffic?: boolean;
}

// --- performance -------------------------------------------------------------

export interface CampaignMetrics {
  engagements: number;
  uniqueDevices: number;
  scans: number;
  /** Distinct registered users who came in through the campaign. */
  registeredPeople: number;
  /** Devices with no registered user behind them (visitors, unfinished signups). */
  unregisteredPeople: number;
  /** Registered people who were already signed up when they came through. A subset of registeredPeople. */
  existingPeople: number;
  signups: number;
  chefsRecruited: number;
  eatersRecruited: number;
  dishesCreated: number;
  /** Percent: signups over unique devices. */
  conversionRatePct: number;
}

export interface ChannelMetrics {
  channel: ChannelBucket;
  engagements: number;
  uniqueDevices: number;
  signups: number;
}

export interface PerformanceRow extends CampaignMetrics {
  campaignId: string;
  slug: string;
  name: string;
  type: CampaignType;
  channel: CampaignChannel;
  status: CampaignStatus;
  localeId?: string;
  localeName?: string;
  startsAt?: string;
  endsAt?: string;
  qrCodes: number;
  channels: ChannelMetrics[] | null;
  counterSignups: number;
  countersInSync: boolean;
}

export interface PerformanceTotals extends CampaignMetrics {
  campaigns: number;
}

export interface CampaignFilters {
  type?: CampaignType;
  channel?: CampaignChannel;
  status?: CampaignStatus;
  localeIds?: string[];
  /** YYYY-MM-DD, profile timezone, inclusive. Bounds engagements, not campaigns. */
  startDate?: string;
  endDate?: string;
}

function filterQuery(f: CampaignFilters) {
  return {
    type: f.type,
    channel: f.channel,
    status: f.status,
    localeIds: f.localeIds?.length ? f.localeIds.join(',') : undefined,
    startDate: f.startDate,
    endDate: f.endDate,
  };
}

export async function listPerformance(
  params: CampaignFilters & PageRequest,
  signal?: AbortSignal,
): Promise<Page<PerformanceRow> & { totals: PerformanceTotals | null }> {
  const res = await get<P1Response<PerformanceRow> & { totals: PerformanceTotals | null }>(
    '/admin/analytics/campaigns',
    { ...filterQuery(params), page: params.page, pageSize: params.pageSize },
    signal,
  );
  return { ...fromP1(res), totals: res.totals };
}

/** One campaign's row; the window bounds engagements (default: the last 30 days). */
export async function getCampaignPerformance(
  id: string,
  window: { startDate?: string; endDate?: string },
  signal?: AbortSignal,
): Promise<PerformanceRow> {
  return (await get<{ performance: PerformanceRow }>(`/admin/campaigns/${id}/performance`, { ...window }, signal))
    .performance;
}

/** The registered people a campaign brought in, as directory rows. P1. */
export async function getCampaignPeople(
  id: string,
  page: PageRequest,
  existing: boolean,
  signal?: AbortSignal,
): Promise<Page<AdminProfileRow>> {
  return fromP1(
    await get<P1Response<AdminProfileRow>>(
      `/admin/campaigns/${id}/people`,
      { ...toP1Query(page), existing: existing ? 'true' : undefined },
      signal,
    ),
  );
}

export function exportPerformance(filters: CampaignFilters): Promise<void> {
  return downloadExport('/admin/analytics/campaigns/export.csv', 'campaigns', filterQuery(filters));
}

// --- library -------------------------------------------------------------------

export interface LibraryParams extends PageRequest {
  type?: CampaignType;
  channel?: CampaignChannel;
  status?: CampaignStatus;
  localeId?: string;
  searchTerm?: string;
}

export async function listCampaigns(params: LibraryParams, signal?: AbortSignal): Promise<Page<Campaign>> {
  return fromP1(await get<P1Response<Campaign>>('/admin/campaigns', { ...params }, signal));
}

export function getCampaign(id: string, signal?: AbortSignal): Promise<CampaignDetail> {
  return get<CampaignDetail>(`/admin/campaigns/${id}`, undefined, signal);
}

export interface CampaignInput {
  slug: string;
  name: string;
  type: CampaignType;
  channel: CampaignChannel;
  localeId: string;
  targetUrl: string;
  utmSource: string;
  utmTerm?: string;
  status?: CampaignStatus;
  deactivatedRedirectUrl?: string;
  /** "I know this is not a link to the app." */
  targetUrlConfirmed?: boolean;
  variants?: Array<{ description: string }>;
}

export function createCampaign(input: CampaignInput): Promise<Campaign> {
  return post<Campaign>('/admin/campaigns', input);
}

export type CampaignPatch = Partial<
  Pick<
    CampaignInput,
    | 'name'
    | 'type'
    | 'channel'
    | 'localeId'
    | 'targetUrl'
    | 'utmSource'
    | 'utmTerm'
    | 'status'
    | 'deactivatedRedirectUrl'
    | 'targetUrlConfirmed'
  >
>;

export function patchCampaign(id: string, body: CampaignPatch): Promise<Campaign> {
  return patch<Campaign>(`/admin/campaigns/${id}`, body);
}

export function duplicateCampaign(id: string, slug: string, name?: string): Promise<Campaign> {
  return post<Campaign>(`/admin/campaigns/${id}/duplicate`, { slug, name });
}

export interface DeactivateResult {
  campaign: Campaign;
  qrCodesAffected: number;
  warnings: string[] | null;
}

export function deactivateCampaign(id: string): Promise<DeactivateResult> {
  return post<DeactivateResult>(`/admin/campaigns/${id}/deactivate`);
}

/** Draft → active: its links start carrying UTM and counting. */
export function activateCampaign(id: string): Promise<Campaign> {
  return post<Campaign>(`/admin/campaigns/${id}/activate`);
}

/** Archived → active: its links go back to the target and collect attribution. */
export function unarchiveCampaign(id: string): Promise<Campaign> {
  return post<Campaign>(`/admin/campaigns/${id}/unarchive`);
}

export interface CampaignSettings {
  /** The consumer app's origin. A target elsewhere needs `targetUrlConfirmed`. */
  appDomain: string;
  /** `<appDomain>/r/` — every short link starts with this. */
  shortLinkBase: string;
}

export function getCampaignSettings(signal?: AbortSignal): Promise<CampaignSettings> {
  return get<CampaignSettings>('/admin/campaigns/settings', undefined, signal);
}

export async function listSources(q: string, signal?: AbortSignal): Promise<string[]> {
  return (await get<{ items: string[] | null }>('/admin/campaigns/sources', { q }, signal)).items ?? [];
}

// --- variants & links ---------------------------------------------------------------

export function addVariant(id: string, description: string): Promise<Campaign> {
  return post<Campaign>(`/admin/campaigns/${id}/variants`, { description });
}

export function patchVariant(
  id: string,
  value: string,
  body: { description?: string; disabled?: boolean },
): Promise<Campaign> {
  return patch<Campaign>(`/admin/campaigns/${id}/variants/${value}`, body);
}

/** Get-or-create the short link for (variant, lang). Idempotent. */
export function ensureLink(id: string, variant: string, lang: string): Promise<CampaignLink> {
  return post<CampaignLink>(`/admin/campaigns/${id}/links`, { variant, lang });
}

// --- QR codes -------------------------------------------------------------------------

export interface GenerateQRInput {
  type: 'campaign' | 'marketing';
  campaignId: string;
  variant: string;
  color: 'primary' | 'secondary';
  lang: string;
  connectionLevel?: number;
  connectionGroup?: string;
  connectionProfile?: string;
}

// --- QR code image ---------------------------------------------------------------

/**
 * `<slug text>[_<variant>][_secondary][_<lang>]_<MMDDYY>.png`: the campaign
 * ID (text_MMDDYY) with what tells this code apart from the campaign's other
 * codes. The base, primary, English code is just `<campaign ID>.png`.
 */
export function qrFileName(r: Pick<QRInventoryRow, 'campaignSlug' | 'placement' | 'color' | 'lang'>): string {
  const slug = r.campaignSlug || 'qr_code';
  const m = /^(.*)_(\d{6})$/.exec(slug);
  const [text, date] = m ? [m[1], m[2]] : [slug, ''];
  const parts = [text, r.placement, r.color === 'secondary' ? 'secondary' : '', (r.lang ?? '').toLowerCase()];
  return `${parts.filter(Boolean).join('_')}${date ? `_${date}` : ''}.png`;
}

/** The File System Access save dialog (Chrome, Edge); absent elsewhere. */
type SaveFilePicker = (options: {
  suggestedName: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}) => Promise<{ createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }>;

/**
 * Saves the code's PNG (a public storage URL — no API session involved) under
 * `filename`. Asks where with the browser's save dialog where there is one,
 * else downloads it. Resolves `false` when the admin cancels the dialog.
 */
export async function saveQrImage(publicUrl: string, filename: string): Promise<boolean> {
  const picker = (window as unknown as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  if (picker) {
    let handle: Awaited<ReturnType<SaveFilePicker>>;
    try {
      // First, while the click still counts as a user gesture.
      handle = await picker({
        suggestedName: filename,
        types: [{ description: 'PNG image', accept: { 'image/png': ['.png'] } }],
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return false;
      throw e;
    }
    const writable = await handle.createWritable();
    await writable.write(await fetchQrImage(publicUrl));
    await writable.close();
    return true;
  }
  saveBlob(await fetchQrImage(publicUrl), filename);
  return true;
}

async function fetchQrImage(publicUrl: string): Promise<Blob> {
  const res = await fetch(publicUrl);
  if (!res.ok) throw new Error(`Couldn't download the QR code image (${res.status}).`);
  return res.blob();
}

export interface GenerateQRResult {
  qrCode: { id: string; publicUrl: string };
  ref: string;
  shortUrl: string;
}

export function generateQR(input: GenerateQRInput): Promise<GenerateQRResult> {
  return post<GenerateQRResult>('/admin/qr/generate', input);
}

export async function listQRInventory(
  params: PageRequest & { campaignId?: string },
  signal?: AbortSignal,
): Promise<Page<QRInventoryRow>> {
  return fromP1(await get<P1Response<QRInventoryRow>>('/admin/qr-codes/inventory', { ...params }, signal));
}
